import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import {
    generateUrl,
    getCloudinaryPathFromCloudinaryUrl
} from '../HBMediaElement/HBMediaElement';
import HBText, { TTextType } from '../HBText/HBText';
import type { TExtendedSize } from '../../tokens/types';
import { vars } from '../../theme';
import { resetInteractiveElement } from '../../styles/mixins';
import { HBAvatarGroupContext } from '../HBAvatarGroup/HBAvatarGroupContext';

interface IAvatarSizeAttributes {
    avatarSize: number;
    textType: TTextType;
}

const sizes: Record<TExtendedSize, IAvatarSizeAttributes> = {
    xs: { avatarSize: 16, textType: 'emphasis10' },
    sm: { avatarSize: 24, textType: 'emphasis10' },
    md: { avatarSize: 32, textType: 'emphasis12' },
    lg: { avatarSize: 40, textType: 'emphasis14' },
    xl: { avatarSize: 80, textType: 'emphasis24' }
};

const getAvatarUrlTransformation = ({
    imageSize,
    profileImageUrl
}: {
    imageSize: number;
    profileImageUrl: string;
}) => {
    const path = getCloudinaryPathFromCloudinaryUrl(profileImageUrl);
    if (!path) {
        return profileImageUrl;
    }
    const avatarTransformations = [
        {
            flags: 'lossy',
            crop: 'lfill',
            width: imageSize,
            height: imageSize,
            quality: 'auto:best',
            fetch_format: 'auto'
        },
        { dpr: 2 }
    ];
    return generateUrl({ path, transformations: avatarTransformations });
};

export function isDefaultProfileImage(profileImageUrl: string) {
    return (
        !profileImageUrl ||
        profileImageUrl.includes(
            '/honeybook_cdn/assets_users_production/users/'
        ) ||
        profileImageUrl.includes(
            'assets_users_production/users/empty_default_image.png'
        ) ||
        profileImageUrl.includes(
            'assets_users_production/users/sample_user/profilepic180x180.png'
        )
    );
}

export function getInitialsFromName(fullName: string): string {
    const trimmed = fullName.trim();
    if (!trimmed) {
        return '';
    }

    const parts = trimmed.split(/\s+/);

    if (parts.length === 1) {
        return trimmed[0];
    }

    // First + Last: "Mary Jane Watson" → "MW"
    return parts[0][0] + parts.at(-1)[0];
}

export interface HBAvatarProps {
    /** Full name used to generate initials and provide accessible label (e.g., "Jane Doe" → "JD") */
    name?: string;
    /** Cloudinary image URL; default/placeholder URLs are detected and replaced with initials. */
    profileImageUrl?: string;
    /** Custom content that replaces the generated initials display. */
    children?: React.ReactNode;
    /** Defaults to 'md', or inherits from HBAvatarGroupContext if inside an HBAvatarGroup. */
    size?: TExtendedSize;
    /** CSS class forwarded to the root avatar element. */
    className?: string;
    /** Override the accessible label for special cases */
    ariaLabel?: string;
    /** `data-testid` applied to the root element. */
    testId?: string;
    /** @deprecated Use `name` prop instead. Directly supplies the initials string; bypasses the automatic generation from `name`. */
    userInitials?: string;
    /** Click handler; when provided, renders the avatar as a `<button>` element. */
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** When provided, renders as an anchor element. When disabled, the href is removed and aria-disabled is set. */
    href?: string;
    /** Link target for the anchor variant. Defaults to `_self`. */
    target?: '_blank' | '_self';
    /** `rel` attribute forwarded to the anchor element. */
    rel?: string;
    /** When true, blocks interaction and applies reduced opacity. For the non-interactive variant, sets `data-disabled`; for button/link, uses the native disabled mechanism. */
    disabled?: boolean;
    /** Adds a selected ring around the avatar via box-shadow. */
    selected?: boolean;
}

const HBAvatar = ({
    className,
    size,
    profileImageUrl,
    testId,
    userInitials,
    onClick,
    href,
    target,
    rel,
    disabled,
    selected,
    name,
    ariaLabel,
    children
}: HBAvatarProps) => {
    const groupContext = useContext(HBAvatarGroupContext);
    const effectiveSize = size ?? groupContext?.size ?? 'md';

    const imageSize = sizes[effectiveSize].avatarSize;
    const textType = sizes[effectiveSize].textType;

    const imageUrl =
        profileImageUrl && !isDefaultProfileImage(profileImageUrl)
            ? getAvatarUrlTransformation({
                  imageSize,
                  profileImageUrl
              })
            : undefined;

    const derivedInitials = name
        ? getInitialsFromName(name)
        : userInitials || '';

    const userInitialsToShow = (
        effectiveSize === 'xs' ? derivedInitials.charAt(0) : derivedInitials
    ).toUpperCase();

    const displayContent = children ?? userInitialsToShow;
    const accessibleLabel = ariaLabel || name || userInitials;

    const content = !imageUrl && (
        // aria-label used on parent element to provide accessible label for the avatar
        <HBText color="text.primary" type={textType}>
            <span aria-hidden="true">{displayContent}</span>
        </HBText>
    );

    const commonProps = {
        'aria-label': accessibleLabel,
        'data-testid': testId,
        'data-selected': selected || undefined,
        className,
        $imageSize: imageSize,
        $profileImageUrl: imageUrl,
        $isUpToSmallSize: effectiveSize === 'xs' || effectiveSize === 'sm'
    };

    if (href) {
        return (
            <AvatarLink
                {...commonProps}
                href={disabled ? undefined : href}
                target={target}
                rel={rel}
                aria-disabled={disabled ? 'true' : undefined}
            >
                {content}
            </AvatarLink>
        );
    }

    if (onClick) {
        return (
            <AvatarButton
                {...commonProps}
                onClick={onClick}
                disabled={disabled}
                type="button"
            >
                {content}
            </AvatarButton>
        );
    }

    return (
        <AvatarContainer
            {...commonProps}
            data-disabled={disabled ? 'true' : undefined}
            role="img"
        >
            {content}
        </AvatarContainer>
    );
};

export default HBAvatar;

// -------------------------------- Styles -------------------------------- //

const BG_COLOR = vars.colors.surface.itemStrong;
const SEPARATOR_COLOR = vars.colors.surface.item;
const FOCUS_COLOR = vars.colors.stroke.keyboardFocus;
const SELECTED_COLOR = vars.colors.stroke.selected;

const SELECTED_RING = `0 0 0 2px ${SELECTED_COLOR}`;
const SEPARATOR_RING = `0 0 0 4px ${SEPARATOR_COLOR}`;
const FOCUS_RING = `0 0 0 6px ${FOCUS_COLOR}`;

const baseAvatarStyles = css<{
    $imageSize: number;
    $profileImageUrl: string;
    $isUpToSmallSize: boolean;
}>`
    border-radius: ${vars.radius.full};
    box-sizing: border-box;
    border: 2px solid ${SEPARATOR_COLOR};
    width: ${props => props.$imageSize}px;
    height: ${props => props.$imageSize}px;
    min-width: ${props => props.$imageSize}px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    user-select: none;
    cursor: default;

    /* Prevents layout shifts when wrapped in inline containers (e.g., tooltips) */
    vertical-align: middle;

    ${({ $isUpToSmallSize, $profileImageUrl }) => css`
        ${$isUpToSmallSize &&
        css`
            border-width: 1px;
        `}
        ${$profileImageUrl
            ? css`
                  background-position: center;
                  background-position: center; /* Do not remove this duplicate. This is fixing a very weird bug */
                  background-size: cover;
                  background-image: url(${$profileImageUrl});
              `
            : css`
                  background-color: ${BG_COLOR};
              `}
    `}

    &[data-selected] {
        box-shadow: ${SELECTED_RING};
    }
`;

const interactiveAvatarStyles = css<{
    $profileImageUrl: string;
}>`
    ${resetInteractiveElement}
    ${baseAvatarStyles}
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${FOCUS_COLOR};
        outline-offset: 0;
    }

    &[data-selected]:focus-visible {
        box-shadow: ${SELECTED_RING}, ${SEPARATOR_RING}, ${FOCUS_RING};
        outline: none;
    }

    &[data-disabled='true'],
    &[aria-disabled='true'],
    &:disabled {
        opacity: 0.2;
        cursor: not-allowed;
    }

    /* Ensure background color persists on hover (override global styles) */
    ${({ $profileImageUrl }) =>
        !$profileImageUrl &&
        css`
            &:hover,
            &:active,
            &:focus {
                background-color: ${BG_COLOR};
            }
        `}
`;

interface AvatarBaseProps {
    $imageSize: number;
    $profileImageUrl: string;
    $isUpToSmallSize: boolean;
}

const AvatarButton = styled.button<AvatarBaseProps>`
    ${interactiveAvatarStyles}
`;

const AvatarLink = styled.a<AvatarBaseProps>`
    ${interactiveAvatarStyles}
`;

const AvatarContainer = styled.div<AvatarBaseProps>`
    ${baseAvatarStyles}
    &[data-disabled='true'] {
        opacity: 0.2;
    }
`;
